import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import SaveIcon from "@mui/icons-material/Save";
import Title from "./Title";
import { Localized } from "@fluent/react";
import { InviteSummary } from "../../types/apiv1";
import { saveAs } from 'file-saver';
import { Box, Button, TableContainer, IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";

interface InviteSummaryListProps {
  users: InviteSummary[];
  onDeleteInvite: (id: string) => void;
  onResendInvitationMail: (email: string, org_id: string) => void;
}

interface SortConfig {
  key: keyof InviteSummary;
  direction: 'asc' | 'desc';
}

export default function InviteSummaryList(props: InviteSummaryListProps) {
  const [sortConfig, setSortConfig] = React.useState<SortConfig>({
    key: 'invitee_email',
    direction: 'asc',
  });

  const sortedUsers = React.useMemo(() => {
    const sorted = [...props.users].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    return sorted;
  }, [props.users, sortConfig]);

  const formattedDate = (dateString: string) => {
    const currentDate = new Date(dateString);
    return `${currentDate.getFullYear()}-${pad(currentDate.getMonth() + 1)}-${pad(currentDate.getDate())} ${pad(currentDate.getHours())}:${pad(currentDate.getMinutes())}:${pad(currentDate.getSeconds())}`;
  };

  function pad(number: number) {
    return number < 10 ? '0' + number : number;
  }

  const requestSort = (key: keyof InviteSummary) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const exportToCSV = () => {
    const headers = Object.keys(sortedUsers[0]);
    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...sortedUsers.map((row: InviteSummary) => headers.map(header => row[header as keyof InviteSummary]).join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    saveAs(encodedUri, "userData.csv");
  };

  const resendInvitationMail = (email: string, org_name: string, org_id: string) => {
    const confirmation = window.confirm(`Are you sure you want to resend the invitation to ${email} for organization: ${org_name}?`);
    if (confirmation) {
      props.onResendInvitationMail(email, org_id);
    }
  };

  const deleteInvite = (inviteId: string, email: string, org_name: string) => {
    const confirmation = window.confirm(`Are you sure you want to delete the invitation to ${email} for organization: ${org_name}?`);
    if (confirmation) {
      props.onDeleteInvite(inviteId);
    }
  };

  return (
    <React.Fragment>
      <Box sx={{ mb: 2 }}>
        <Localized id="invite-summary-list-title">
          <Title />
        </Localized>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button onClick={exportToCSV} variant="outlined" startIcon={<SaveIcon />}>
          <Localized id="invite-summary-export-to-csv"></Localized>
        </Button>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell onClick={() => requestSort('invitee_email')}>
                <Localized id="invite-summary-invitee-email">Email</Localized>
              </TableCell>
              <TableCell onClick={() => requestSort('invitee_role')}>
                <Localized id="invite-summary-role">Role</Localized>
              </TableCell>
              <TableCell onClick={() => requestSort('org_name_invited_to')}>
                <Localized id="invite-summary-org-name">Org Invited To</Localized>
              </TableCell>
              <TableCell onClick={() => requestSort('invitation_expires_at')}>
                <Localized id="invite-summary-expires-at">Expires At</Localized>
              </TableCell>
              <TableCell>
                <Localized id="invite-summary-actions">Actions</Localized>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.map((row) => (
              <TableRow key={row.invite_id}>
                <TableCell>{row.invitee_email}</TableCell>
                <TableCell>{row.invitee_role}</TableCell>
                <TableCell>{row.org_name_invited_to}</TableCell>
                <TableCell>{formattedDate(row.invitation_expires_at)}</TableCell>
                <TableCell>
                  <KebabMenu
                    onResend={() => resendInvitationMail(row.invitee_email, row.org_name_invited_to, row.org_id_invited_to)}
                    onDelete={() => deleteInvite(row.invite_id, row.invitee_email, row.org_name_invited_to)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </React.Fragment>
  );
}

interface KebabMenuProps {
  onResend: () => void;
  onDelete: () => void;
}

const KebabMenu: React.FC<KebabMenuProps> = ({ onResend, onDelete }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <IconButton
        aria-label="more"
        aria-controls="long-menu"
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="long-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={() => { handleClose(); onResend(); }}>
          <Localized id="invite-summary-resend-email">Resend</Localized>
        </MenuItem>
        <MenuItem onClick={() => { handleClose(); onDelete(); }}>
          <Localized id="invite-summary-delete">Delete</Localized>
        </MenuItem>
      </Menu>
    </div>
  );
};
